import { Check, ChevronLeft, ChevronRight } from "lucide-react";

type SectorProgressItem = {
  sectorId: string;
  sectorCode: string;
  sectorName: string;
  status: "completed" | "current" | "upcoming" | "skipped";
};

type RiskWizardSectorProgressProps = {
  sectors: SectorProgressItem[];
  currentIndex: number;
  onPrevSector: () => void;
  onNextSector: () => void;
  onSectorChange: (index: number) => void;
};

function getPillStyle(
  status: SectorProgressItem["status"],
  isSelected: boolean
): string {
  if (isSelected && status === "completed")
    return "bg-blue-600 text-white ring-2 ring-blue-300 ring-offset-2";
  if (status === "current")
    return "bg-blue-600 text-white ring-2 ring-blue-300";
  if (status === "completed") return "bg-blue-900 text-white";
  return "bg-secondary text-muted-foreground";
}

export function RiskWizardSectorProgress({
  sectors,
  currentIndex,
  onPrevSector,
  onNextSector,
  onSectorChange,
}: RiskWizardSectorProgressProps) {
  const currentSector = sectors[currentIndex];
  const completedCount = sectors.filter((s) => s.status === "completed").length;
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === sectors.length - 1;

  return (
    <div className="grid gap-3 md:gap-4">
      <div className="flex items-center justify-between">
        <div>
          <span className="text-xl font-bold md:text-2xl">
            {currentSector?.sectorName}
          </span>
          <p className="text-xs text-muted-foreground">
            Sector {currentIndex + 1} din {sectors.length} &middot;{" "}
            {completedCount} completate
          </p>
        </div>

        <div className="flex gap-1">
          <button
            type="button"
            onClick={onPrevSector}
            disabled={isFirst}
            className="p-1.5 rounded border border-border hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            aria-label="Sectorul anterior"
          >
            <ChevronLeft className="h-4 w-4 md:h-5 md:w-5" />
          </button>
          <button
            type="button"
            onClick={onNextSector}
            disabled={isLast}
            className="p-1.5 rounded border border-border hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            aria-label="Sectorul urmator"
          >
            <ChevronRight className="h-4 w-4 md:h-5 md:w-5" />
          </button>
        </div>
      </div>

      <div className="flex gap-1.5 overflow-x-auto py-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        {sectors.map((sector, index) => (
          <button
            key={sector.sectorId}
            type="button"
            onClick={() => onSectorChange(index)}
            className={`shrink-0 flex items-center gap-1.5 h-8 md:h-9 px-3 md:px-4 rounded-full text-xs md:text-sm font-medium cursor-pointer ${getPillStyle(sector.status, index === currentIndex)}`}
            aria-label={`${sector.sectorName} - ${sector.status}`}
            aria-current={index === currentIndex ? "step" : undefined}
          >
            {sector.status === "completed" && (
              <Check className="h-3 w-3 md:h-3.5 md:w-3.5 shrink-0" />
            )}
            <span className="max-w-[120px] md:max-w-[160px] truncate">
              {sector.sectorName}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
